import React, { useCallback, useMemo, useState } from "react";
import { useUserInfo } from "../../utils/security";
import {
    Box,
    Button,
    Chip,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import SideSheet, {
    SideSheetButtons,
    SideSheetContent,
    SideSheetForm,
} from "../../components/SideSheet";
import { LoadingButton } from "@mui/lab";
import { ProjectSelect } from "../projects";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { FormFieldErrors, hasErrors, useForm, validateMandatoryField } from "../../utils/form";
import { EmployeeSelect } from "../employees";
import SliderWithButtons from "../../components/SliderWithButtons";

const formatMinutes = (minutes) => {
    if (minutes < 60) {
        return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
    if (minutes === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${minutes}min`;
};
const TimeChip = ({ minutes, onClick, selected }) => {
    const props = {};
    if (selected) {
        props.color = "primary";
    }
    return (
        <Chip
            label={formatMinutes(minutes)}
            onClick={() => onClick && onClick(Math.floor(minutes / 60), minutes % 60)}
            {...props}
        />
    );
};
const minuteChips = (function () {
    const hours = 8;
    const minuteChips = [];
    for (let i = 0; i < hours; i++) {
        !!i && minuteChips.push(i * 60);
        for (let j = 1; j < 4; j++) {
            minuteChips.push(i * 60 + j * 15);
        }
    }
    minuteChips.push(hours * 60);
    return minuteChips;
})();

export const TimesheetModal = ({ data, onSubmit, open, hide, withProject, withEmployee }) => {
    const [loading, setLoading] = useState(false);
    const { userInfo } = useUserInfo();

    const settings = useMemo(() => {
        return {
            ...(userInfo.timesheetsSettings || {}),
            showProject: userInfo.projectsSupport,
            showEmployee: userInfo.employeesSupport,
            dateFormat: userInfo.dateFormat || "DD.MM.YYYY",
            timeFormat: userInfo.timeFormat || "HH:mm",
            ampm: !!userInfo.ampm,
        };
    }, [userInfo]);

    const onSubmitFn = useCallback(
        async (data) => {
            setLoading(true);
            try {
                onSubmit && (await onSubmit(data));
            } finally {
                setLoading(false);
            }
        },
        [onSubmit, setLoading]
    );

    const validate = useCallback(
        (values, errors) => {
            validateMandatoryField(values.date, "date", errors);

            settings.showProject &&
                settings.projectRequired &&
                validateMandatoryField(values.projectId, "project", errors);
            settings.showEmployee &&
                settings.employeeRequired &&
                validateMandatoryField(values.employeeId, "employee", errors);
            settings.descriptionRequired &&
                validateMandatoryField(values.description, "description", errors);

            validateMandatoryField(values.mode, "mode", errors);
            if (values.mode === "DURATION") {
                validateMandatoryField(values.hours, "hours", errors);
                validateMandatoryField(values.minutes, "minutes", errors);

                let mins = (values.minutes || 0) + (values.hours || 0) * 60;
                if (mins > settings.maxHours * 60) {
                    errors.duration = `Maximální doba položky výkazu je ${settings.maxHours}h`;
                }

                //TODO validate maxHours
            } else if (values.mode === "FROM_TO") {
                validateMandatoryField(values.startTime, "startTime", errors);
                validateMandatoryField(values.endTime, "endTime", errors);

                if (values.startTime && values.endTime && values.endTime < values.startTime) {
                    errors["endTime"] = "Čas do musí být větší než od";
                }

                //TODO validate maxHours
            }
            return errors;
        },
        [settings]
    );

    const { handleSubmit, handleChange, values, setValues, errors } = useForm({
        onSubmit: onSubmitFn,
        validate,
        data,
    });

    const chips = useMemo(() => {
        const maxMinutes = settings.maxHours * 60;
        return minuteChips.filter((v) => v <= maxMinutes);
    }, [settings]);
    const selectedMinutes = values.hours * 60 + values.minutes;

    return (
        <>
            <SideSheet title="Položka výkazu" open={open} hide={hide} loading={loading}>
                <SideSheetForm onSubmit={handleSubmit}>
                    <SideSheetContent>
                        <Box mb={2}>
                            <DatePicker
                                onChange={(date) => setValues({ ...values, date })}
                                value={values.date || ""}
                                disabled={loading}
                                label="Datum"
                                format={settings.dateFormat}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        required: true,
                                        error: hasErrors(errors, "date"),
                                    },
                                }}
                            />
                            <FormFieldErrors errors={errors} field="date" />
                        </Box>
                        {/*
            <FormControl margin="normal" error={hasErrors(errors, "description")} fullWidth>
                <InputLabel htmlFor="component-outlined">Name</InputLabel>
                <OutlinedInput id="component-outlined" defaultValue="Composed TextField" label="Name" />
                <FormFieldErrors errors={errors} field="description" />
                <FormHelperText>Lorem ipsum</FormHelperText>
            </FormControl>
*/}
                        {!!(withProject && settings.showProject) && (
                            <ProjectSelect
                                label="Projekt"
                                fullWidth
                                margin="normal"
                                disabled={loading}
                                required={settings.projectRequired}
                                error={hasErrors(errors, "project")}
                                helperText={<FormFieldErrors errors={errors} field="project" />}
                            />
                        )}
                        {!!(withEmployee && settings.showEmployee) && (
                            <EmployeeSelect
                                label="Pracovník"
                                fullWidth
                                margin="normal"
                                disabled={loading}
                                required={settings.employeeRequired}
                            />
                        )}

                        <Box sx={{ py: 2, textAlign: "center" }}>
                            <ToggleButtonGroup
                                exclusive
                                value={values.mode || "DURATION"}
                                onChange={(e, value) => setValues({ ...values, mode: value })}
                                disabled={!!loading}
                                fullWidth
                                margin="normal">
                                <ToggleButton value="DURATION">
                                    <TimerOutlinedIcon /> Doba
                                </ToggleButton>
                                <ToggleButton value="FROM_TO">
                                    <ScheduleOutlinedIcon /> Čas od-do
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {(values.mode === "DURATION" || !values.mode) && (
                            <Box mb={2}>
                                <Box py={2}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: 0.5,
                                            justifyContent: "center",
                                        }}>
                                        {chips.map((minutes) => (
                                            <TimeChip
                                                key={minutes}
                                                minutes={minutes}
                                                selected={minutes === selectedMinutes}
                                                onClick={(hours, minutes) =>
                                                    setValues({ ...values, hours, minutes })
                                                }
                                            />
                                        ))}
                                    </Box>
                                </Box>
                                <Box mb={2}>
                                    <SliderWithButtons
                                        value={values.hours}
                                        onChange={handleChange}
                                        name="hours"
                                        title="Hodiny"
                                        unit="h"
                                        minValue={0}
                                        maxValue={settings.maxHours || 24}
                                        step={1}
                                        disabled={loading}
                                    />
                                </Box>

                                <Box mb={2}>
                                    <SliderWithButtons
                                        value={values.minutes}
                                        onChange={handleChange}
                                        name="minutes"
                                        title="Minuty"
                                        unit="min"
                                        minValue={0}
                                        maxValue={settings.roundTo15 ? 45 : 59}
                                        step={settings.roundTo15 ? 15 : 1}
                                        disabled={loading}
                                    />
                                </Box>
                                <FormFieldErrors errors={errors} field="duration" />
                            </Box>
                        )}
                        {values.mode === "FROM_TO" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: 1,
                                    alignItems: "center",
                                }}>
                                <Box sx={{ flex: "1 1 auto" }}>
                                    <TimePicker
                                        value={values.startTime}
                                        onChange={(startTime) =>
                                            setValues({ ...values, startTime })
                                        }
                                        disabled={loading}
                                        label="Čas od"
                                        minutesStep={settings.roundTo15 ? 15 : undefined}
                                        format={settings.timeFormat}
                                        ampm={settings.ampm}
                                        slotProps={{
                                            field: {
                                                fullWidth: true,
                                                margin: "normal",
                                                error: hasErrors(errors, "startTime"),
                                                helperText: (
                                                    <FormFieldErrors
                                                        errors={errors}
                                                        field="startTime"
                                                    />
                                                ),
                                            },
                                        }}
                                        margin="normal"
                                        required
                                    />
                                </Box>
                                <Typography>-</Typography>
                                <Box sx={{ flex: "1 1 auto" }}>
                                    <TimePicker
                                        value={values.endTime}
                                        onChange={(endTime) => setValues({ ...values, endTime })}
                                        disabled={loading}
                                        label="Čas do"
                                        minutesStep={settings.roundTo15 ? 15 : undefined}
                                        format={settings.timeFormat}
                                        ampm={settings.ampm}
                                        slotProps={{
                                            field: {
                                                fullWidth: true,
                                                margin: "normal",
                                                error: hasErrors(errors, "endTime"),
                                                helperText: (
                                                    <FormFieldErrors
                                                        errors={errors}
                                                        field="endTime"
                                                    />
                                                ),
                                            },
                                        }}
                                        margin="normal"
                                        required
                                    />
                                </Box>
                            </Box>
                        )}

                        <TextField
                            value={values.description}
                            onChange={handleChange}
                            disabled={loading}
                            name="description"
                            label="Popis"
                            placeholder="Popište, co jste dělali..."
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                            required={settings.descriptionRequired}
                            helperText={<FormFieldErrors errors={errors} field="description" />}
                        />
                        {/*<pre style={{ color: "red" }}>{JSON.stringify(errors, null, 4)}</pre>*/}
                    </SideSheetContent>
                    <SideSheetButtons>
                        <Button
                            variant="outlined"
                            onClick={(e) => hide && hide()}
                            disabled={loading}>
                            Storno
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            loading={loading}>
                            Uložit
                        </LoadingButton>
                    </SideSheetButtons>
                </SideSheetForm>
            </SideSheet>
        </>
    );
};
